import { Fragment, type ReactNode } from 'react';
import { ReferenceChip } from '@/features/agentic-insights/components/components/ReferenceChip';
import { useFindingRefsLookup } from '@/features/agentic-insights/components/data/FindingRefsContext';
import { humanizeEnums } from '@/features/agentic-insights/components/data/humanizeEnums';
import { parseReferences } from '@/features/agentic-insights/components/data/types';
import { ObjectiveLink } from '../ui/ObjectiveLink';
import { splitCodeTokens } from './codeTokens';
import { stripReferenceEcho } from './referenceEcho';

/**
 * How a resolved reference is presented.
 *
 * `emphasis` — an emphasised phrase inside the sentence, going nowhere. The default, and what a
 * claim wants: a chip breaks the line it sits in and turns a claim into a form, and a link invites a
 * reader out of a sentence they are halfway through.
 *
 * `link` — the same phrase, linked. For a value that IS the reference, like an action's applies-to,
 * where the name is the thing you want to go and look at.
 *
 * `chip` — the classic tab's `ReferenceChip`: a badge, objectives linked, items labelled. For the
 * apparatus behind a finding, where the ids are the point. A reader working through the reasoning is
 * checking the run against the portfolio, so the objective it names has to be somewhere they can go
 * — and the surface should not have two ways of drawing a reference for the same job.
 */
export type ReferenceStyle = 'emphasis' | 'link' | 'chip';

interface RunProseProps {
  children: string;
  references?: ReferenceStyle;
}

/**
 * The code span. Mono and boxed, the way a backtick reads in a chat message — deliberately NOT this
 * surface's mono meta idiom, which is uppercase and widely tracked: a tool name is a value to be
 * copied exactly, not a label.
 */
const CODE_CLASS =
  'rounded-sm bg-bg-neutral-subtle px-1 font-mono text-[0.9em] text-text-neutral-primary';

/**
 * A finding's free text, with every `[item:id]` / `[objective:id]` token resolved to the real label
 * and every machine name set as code.
 *
 * The resolution itself is the shared data lookup, but the rendering is this surface's own: a
 * reference reads as an emphasised phrase inside the sentence rather than as a chip. A chip breaks
 * the line it sits in and turns a claim into a form; the whole point of a claim is that it reads as
 * one sentence.
 *
 * Machine names are the exception, because they are not words. A source statement reading
 * "get_items_health_summary with serves_objective_ids:[68] rolls up to total 1 item" is a sentence
 * with two tokens in it that a reader has to stop and parse as identifiers; set as code they can be
 * taken in at a glance and skipped past. They are recognised by shape (see `codeTokens`), since the
 * contract does not mark them.
 */
export const RunProse = ({ children, references = 'emphasis' }: RunProseProps) => {
  const refs = useFindingRefsLookup();
  /**
   * The agent names a reference and then names it again — `[objective:68] Increase Digital Revenue`
   * — so a resolved token would print the title and the sentence would print it straight after.
   * Stripped here rather than at one call site: the habit is in every field it writes, not just in a
   * description block's lead.
   */
  const text = stripReferenceEcho(children, refs);
  const tokens = parseReferences(text);

  const label = (kind: 'item' | 'objective', id: number): string => {
    if (kind === 'objective') return refs.objective(id) ?? `Objective ${id}`;
    const item = refs.item(id);
    if (!item) return `Item ${id}`;
    return [item.externalKey, item.name].filter(Boolean).join(' · ') || `Item ${id}`;
  };

  /**
   * A stretch of the run's own prose: the machine names set as code, everything between them read as
   * words — which is where the enum humanising belongs, so a token like `get_objective_health` is
   * never rewritten as English.
   */
  const plain = (text: string, keyPrefix: string): ReactNode[] =>
    splitCodeTokens(text).map((part) =>
      part.kind === 'code' ? (
        <code
          key={`${keyPrefix}-${part.offset}`}
          data-testid="agentic-x-code"
          className={CODE_CLASS}
        >
          {part.value}
        </code>
      ) : (
        <Fragment key={`${keyPrefix}-${part.offset}`}>{humanizeEnums(part.value)}</Fragment>
      ),
    );

  if (tokens.length === 0) return <>{plain(text, 't-0')}</>;

  const nodes: ReactNode[] = [];
  let cursor = 0;

  for (const token of tokens) {
    if (token.index > cursor) {
      nodes.push(
        <Fragment key={`t-${cursor}`}>
          {plain(text.slice(cursor, token.index), `t-${cursor}`)}
        </Fragment>,
      );
    }
    if (references === 'chip') {
      nodes.push(<ReferenceChip key={`r-${token.index}`} kind={token.kind} id={token.id} />);
      cursor = token.index + token.raw.length;
      continue;
    }

    const resolved = label(token.kind, token.id);
    const isLinkedObjective =
      references === 'link' && token.kind === 'objective' && refs.objective(token.id) !== undefined;

    nodes.push(
      <span
        key={`r-${token.index}`}
        data-testid="agentic-x-reference"
        className={
          isLinkedObjective
            ? 'font-medium'
            : 'font-medium text-text-neutral-primary underline decoration-border-neutral-subtle decoration-1 underline-offset-2'
        }
      >
        {/* Only an objective whose title actually resolved: a label reading "Objective 68" has
            nothing to open, and should not look clickable. */}
        {isLinkedObjective ? <ObjectiveLink>{resolved}</ObjectiveLink> : resolved}
      </span>,
    );
    cursor = token.index + token.raw.length;
  }

  if (cursor < text.length) {
    nodes.push(<Fragment key={`t-${cursor}`}>{plain(text.slice(cursor), `t-${cursor}`)}</Fragment>);
  }

  return <>{nodes}</>;
};
